import { create } from 'zustand';
import type { MailListFilter, MailListFilterState } from '../types/mail.types';

export type ComposeMode = 'new' | 'reply' | 'replyAll' | 'forward' | 'editDraft';
export type InboxTab = 'focused' | 'other';
export type ViewMode = 'flat' | 'threaded';

// ─── View-menu preferences (Outlook "View" ribbon parity) ───────────────────
export type FolderPaneMode = 'normal' | 'minimized' | 'hidden';
export type LayoutMode = 'right' | 'bottom' | 'off';
export type ReadingPaneTheme = 'auto' | 'light' | 'dark';

const SIDEBAR_WIDTH_STORAGE_KEY = 'edgemail.folderPane.width';
const SIDEBAR_MIN_WIDTH = 220;
const SIDEBAR_MAX_WIDTH = 560;
const DEFAULT_SIDEBAR_WIDTH = 240;

function clampSidebarWidth(width: number): number {
  if (!Number.isFinite(width)) return DEFAULT_SIDEBAR_WIDTH;
  return Math.max(SIDEBAR_MIN_WIDTH, Math.min(SIDEBAR_MAX_WIDTH, Math.round(width)));
}

function readStoredSidebarWidth(): number {
  if (typeof window === 'undefined') return DEFAULT_SIDEBAR_WIDTH;
  const raw = window.localStorage.getItem(SIDEBAR_WIDTH_STORAGE_KEY);
  return raw ? clampSidebarWidth(Number(raw)) : DEFAULT_SIDEBAR_WIDTH;
}

function storeSidebarWidth(width: number): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(SIDEBAR_WIDTH_STORAGE_KEY, String(clampSidebarWidth(width)));
}

export type OverlayKind =
  | 'shortcutHelp'
  | 'createFolder'
  | 'gotoFolder'
  | 'moveToFolder'
  | 'flagDate';

export interface SenderAutomationPrompt {
  messageId: string;
  targetFolderId: string;
  automationType: 'sender' | 'conversation';
  senderEmail: string;
  senderName?: string | null;
  counterpartEmail?: string | null;
  counterpartName?: string | null;
  sourceThreadId?: string | null;
  targetFolderName: string;
  targetFolderPath: string;
  matchingExistingCount: number;
  autoFileRuleExists: boolean;
}

export interface RuleBuilderPrompt {
  messageId: string;
}

interface MailClientState {
  // Layout
  sidebarWidth: number;
  sidebarCollapsed: boolean;
  messageListWidth: number;

  // Selection
  activeAccountId: string | null;
  activeFolderId: string | null;
  selectedMessageIds: string[];
  activeMessageId: string | null;

  // Tabs
  activeTab: InboxTab;

  // Compose
  composeOpen: boolean;
  composeMode: ComposeMode | null;
  composeReplyToId: string | null;

  // Notifications panel (EdgeMail)
  notificationsPanelOpen: boolean;

  // View mode
  viewMode: ViewMode;

  // Search
  searchQuery: string;
  searchActive: boolean;

  // Message list filters
  messageFilters: MailListFilterState;

  // Keyboard-driven overlays (shortcut help, folder picker, flag date, etc.)
  overlay: OverlayKind | null;
  senderAutomationPrompt: SenderAutomationPrompt | null;
  ruleBuilderPrompt: RuleBuilderPrompt | null;

  // Search-jump context. When the user commits a search result that lives in
  // a different folder, we stash the previous folder so a "Back to X" pill
  // can restore it, and we flag the landed message id so the row can flash.
  searchReturnFolderId: string | null;
  searchLandedMessageId: string | null;

  // ─── File menu (Outlook "File" ribbon parity) ─────────────────────────────
  fileRibbonOpen: boolean;          // second-row File ribbon visibility

  // ─── View menu (Outlook "View" ribbon parity) ─────────────────────────────
  viewRibbonOpen: boolean;          // the second-row ribbon visible under the menu bar
  folderPaneMode: FolderPaneMode;   // normal | minimized | hidden
  layoutMode: LayoutMode;           // right | bottom | off (reading pane position)
  readingPaneTheme: ReadingPaneTheme; // auto | light | dark (per-reading-pane override)
  messagePreviewVisible: boolean;   // show the 2-line preview under sender/subject
  zoomLevel: number;                // 0.5 … 2.0 multiplier for message content

  // Actions
  setSidebarWidth: (width: number) => void;
  setSidebarCollapsed: (collapsed: boolean) => void;
  setMessageListWidth: (width: number) => void;
  setActiveAccountId: (accountId: string | null) => void;
  setActiveFolder: (folderId: string | null) => void;
  selectMessage: (messageId: string) => void;
  selectMessages: (messageIds: string[]) => void;
  selectRange: (messageIds: string[], activeMessageId: string) => void;
  toggleMessageSelection: (messageId: string) => void;
  clearSelection: () => void;
  setActiveTab: (tab: InboxTab) => void;
  openCompose: (mode: ComposeMode, replyToId?: string) => void;
  closeCompose: () => void;
  openNotificationsPanel: () => void;
  closeNotificationsPanel: () => void;
  toggleNotificationsPanel: () => void;
  setSearchQuery: (query: string) => void;
  setSearchActive: (active: boolean) => void;
  toggleMessageFilter: (filter: MailListFilter) => void;
  clearMessageFilters: () => void;
  setSenderFilter: (sender: string | null) => void;
  setDomainFilter: (domain: string | null) => void;
  setDateFilter: (dateFrom: string | null, dateTo: string | null) => void;
  applySavedMessageFilters: (filters: MailListFilterState) => void;
  setViewMode: (mode: ViewMode) => void;
  toggleViewMode: () => void;
  openOverlay: (kind: OverlayKind) => void;
  closeOverlay: () => void;
  openSenderAutomationPrompt: (prompt: SenderAutomationPrompt) => void;
  closeSenderAutomationPrompt: () => void;
  openRuleBuilderPrompt: (prompt: RuleBuilderPrompt) => void;
  closeRuleBuilderPrompt: () => void;
  jumpToSearchResult: (folderId: string, messageId: string) => void;
  clearSearchReturn: () => void;
  clearSearchLanded: () => void;
  toggleViewRibbon: () => void;
  setViewRibbonOpen: (open: boolean) => void;
  toggleFileRibbon: () => void;
  setFileRibbonOpen: (open: boolean) => void;
  setFolderPaneMode: (mode: FolderPaneMode) => void;
  setLayoutMode: (mode: LayoutMode) => void;
  setReadingPaneTheme: (mode: ReadingPaneTheme) => void;
  setMessagePreviewVisible: (visible: boolean) => void;
  setZoomLevel: (level: number) => void;
  nudgeZoom: (delta: number) => void;
  resetZoom: () => void;
}

export const useMailClientStore = create<MailClientState>((set) => ({
  // Layout defaults
  sidebarWidth: readStoredSidebarWidth(),
  sidebarCollapsed: false,
  messageListWidth: 360,

  // Selection defaults
  activeAccountId: null,
  activeFolderId: null,
  selectedMessageIds: [],
  activeMessageId: null,

  // Tabs
  activeTab: 'focused',

  // Compose
  composeOpen: false,
  composeMode: null,
  composeReplyToId: null,

  // Notifications panel
  notificationsPanelOpen: false,

  // View mode
  viewMode: 'flat' as ViewMode,

  // Search
  searchQuery: '',
  searchActive: false,

  // Message list filters
  messageFilters: { filters: [], sender: null, domain: null, dateFrom: null, dateTo: null },

  // Overlays
  overlay: null,
  senderAutomationPrompt: null,
  ruleBuilderPrompt: null,

  // Search-jump context
  searchReturnFolderId: null,
  searchLandedMessageId: null,

  // File-menu preferences
  fileRibbonOpen: false,

  // View-menu preferences
  viewRibbonOpen: false,
  folderPaneMode: 'normal' as FolderPaneMode,
  layoutMode: 'right' as LayoutMode,
  readingPaneTheme: 'auto' as ReadingPaneTheme,
  messagePreviewVisible: true,
  zoomLevel: 1,

  // Actions
  setSidebarWidth: (width) => {
    const nextWidth = clampSidebarWidth(width);
    storeSidebarWidth(nextWidth);
    set({ sidebarWidth: nextWidth });
  },
  setSidebarCollapsed: (collapsed) =>
    set({ sidebarCollapsed: collapsed }),
  setMessageListWidth: (width) => set({ messageListWidth: width }),
  setActiveAccountId: (accountId) => set({ activeAccountId: accountId }),
  setActiveFolder: (folderId) =>
    set({
      activeFolderId: folderId ?? null,
      selectedMessageIds: [],
      activeMessageId: null,
      // Manual folder change invalidates any "Back to X" context.
      searchReturnFolderId: null,
      searchLandedMessageId: null,
    }),
  selectMessage: (messageId) =>
    set({
      selectedMessageIds: [messageId],
      activeMessageId: messageId,
    }),
  selectMessages: (messageIds) => set({ selectedMessageIds: messageIds }),
  selectRange: (messageIds, activeMessageId) =>
    set({ selectedMessageIds: messageIds, activeMessageId }),
  toggleMessageSelection: (messageId) =>
    set((state) => {
      const exists = state.selectedMessageIds.includes(messageId);
      const newIds = exists
        ? state.selectedMessageIds.filter((id) => id !== messageId)
        : [...state.selectedMessageIds, messageId];
      return {
        selectedMessageIds: newIds,
        activeMessageId: newIds.length === 1 ? newIds[0] : state.activeMessageId,
      };
    }),
  clearSelection: () => set({ selectedMessageIds: [], activeMessageId: null }),
  setActiveTab: (tab) => set({ activeTab: tab }),
  openCompose: (mode, replyToId) =>
    set({
      composeOpen: true,
      composeMode: mode,
      composeReplyToId: replyToId ?? null,
    }),
  closeCompose: () =>
    set({
      composeOpen: false,
      composeMode: null,
      composeReplyToId: null,
    }),
  openNotificationsPanel: () => set({ notificationsPanelOpen: true }),
  closeNotificationsPanel: () => set({ notificationsPanelOpen: false }),
  toggleNotificationsPanel: () =>
    set((state) => ({ notificationsPanelOpen: !state.notificationsPanelOpen })),
  setSearchQuery: (query) => set({ searchQuery: query }),
  setSearchActive: (active) => set({ searchActive: active }),
  toggleMessageFilter: (filter) =>
    set((state) => {
      const exists = state.messageFilters.filters.includes(filter);
      return {
        messageFilters: {
          ...state.messageFilters,
          filters: exists
            ? state.messageFilters.filters.filter((f) => f !== filter)
            : [...state.messageFilters.filters, filter],
        },
      };
    }),
  clearMessageFilters: () => set({ messageFilters: { filters: [], sender: null, domain: null, dateFrom: null, dateTo: null } }),
  setSenderFilter: (sender) =>
    set((state) => ({ messageFilters: { ...state.messageFilters, sender: sender?.trim() || null } })),
  setDomainFilter: (domain) =>
    set((state) => ({ messageFilters: { ...state.messageFilters, domain: domain?.trim() || null } })),
  setDateFilter: (dateFrom, dateTo) =>
    set((state) => ({
      messageFilters: {
        ...state.messageFilters,
        dateFrom: dateFrom?.trim() || null,
        dateTo: dateTo?.trim() || null,
      },
    })),
  applySavedMessageFilters: (filters) =>
    set({
      messageFilters: {
        filters: Array.from(new Set(filters.filters || [])),
        sender: filters.sender || null,
        domain: filters.domain || null,
        dateFrom: filters.dateFrom || null,
        dateTo: filters.dateTo || null,
      },
    }),
  setViewMode: (mode) => set({ viewMode: mode }),
  toggleViewMode: () => set((state) => ({ viewMode: state.viewMode === 'flat' ? 'threaded' : 'flat' })),
  openOverlay: (kind) => set({ overlay: kind }),
  closeOverlay: () => set({ overlay: null }),
  openSenderAutomationPrompt: (prompt) => set({ senderAutomationPrompt: prompt }),
  closeSenderAutomationPrompt: () => set({ senderAutomationPrompt: null }),
  openRuleBuilderPrompt: (prompt) => set({ ruleBuilderPrompt: prompt }),
  closeRuleBuilderPrompt: () => set({ ruleBuilderPrompt: null }),
  jumpToSearchResult: (folderId, messageId) =>
    set((state) => {
      const crossFolder = state.activeFolderId !== null && state.activeFolderId !== folderId;
      return {
        activeFolderId: folderId,
        selectedMessageIds: [messageId],
        activeMessageId: messageId,
        // Only remember the return folder if we actually moved folders.
        searchReturnFolderId: crossFolder ? state.activeFolderId : state.searchReturnFolderId,
        searchLandedMessageId: messageId,
      };
    }),
  clearSearchReturn: () => set({ searchReturnFolderId: null }),
  clearSearchLanded: () => set({ searchLandedMessageId: null }),

  // ─── View menu setters ──────────────────────────────────────────────────
  toggleViewRibbon: () => set((state) => ({ viewRibbonOpen: !state.viewRibbonOpen })),
  setViewRibbonOpen: (open) => set({ viewRibbonOpen: open }),
  toggleFileRibbon: () => set((state) => ({ fileRibbonOpen: !state.fileRibbonOpen })),
  setFileRibbonOpen: (open) => set({ fileRibbonOpen: open }),
  setFolderPaneMode: (mode) =>
    set({
      folderPaneMode: mode,
      // Mirror the legacy boolean so existing shell code keeps working.
      sidebarCollapsed: mode === 'minimized',
    }),
  setLayoutMode: (mode) => set({ layoutMode: mode }),
  setReadingPaneTheme: (mode) => set({ readingPaneTheme: mode }),
  setMessagePreviewVisible: (visible) => set({ messagePreviewVisible: visible }),
  setZoomLevel: (level) =>
    set({ zoomLevel: Math.max(0.5, Math.min(2.0, Math.round(level * 100) / 100)) }),
  nudgeZoom: (delta) =>
    set((state) => ({
      zoomLevel: Math.max(0.5, Math.min(2.0, Math.round((state.zoomLevel + delta) * 100) / 100)),
    })),
  resetZoom: () => set({ zoomLevel: 1 }),
}));
